import type { DocNode } from '@atlaskit/adf-schema/doc';

export type PageRemixLifecycleAdmissionRequest = {
	conversationId?: string;
};

export type ObjectSidebarPageRemixStartRejectionReason =
	| 'adf-load-failed'
	| 'empty-adf'
	| 'malformed-adf'
	| 'stale-adf'
	| 'lifecycle-rejected';

export type ObjectSidebarPageRemixStartResult =
	| {
			status: 'started';
			adfDocument: DocNode;
	  }
	| {
			status: 'rejected';
			reason: ObjectSidebarPageRemixStartRejectionReason;
	  };

type ObjectSidebarPageRemixStartOptions = {
	resolveAdfDocument: () => Promise<DocNode | null>;
	admitLifecycle?: (request: PageRemixLifecycleAdmissionRequest) => boolean;
	conversationId?: string;
	isCurrent?: () => boolean;
	onRejected: (reason: ObjectSidebarPageRemixStartRejectionReason) => void;
};

type UnknownAdfNode = {
	type: string;
	text?: string;
	content?: UnknownAdfNode[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isSchemaValidAdf = async (value: DocNode): Promise<boolean> => {
	try {
		const { validator } = await import(
			/* webpackChunkName: "loadable-adf-utils-validator" */ '@atlaskit/adf-utils/validator'
		);
		return validator()(value).valid;
	} catch {
		return false;
	}
};

const hasMeaningfulAdfContent = (node: UnknownAdfNode): boolean => {
	if (node.type === 'text') {
		return typeof node.text === 'string' && node.text.trim().length > 0;
	}
	if (!Array.isArray(node.content)) {
		return node.type !== 'paragraph' && node.type !== 'doc';
	}
	if (node.type !== 'paragraph' && node.type !== 'doc') {
		return true;
	}
	return node.content.some(hasMeaningfulAdfContent);
};

const rejectStart = (
	reason: ObjectSidebarPageRemixStartRejectionReason,
	onRejected: ObjectSidebarPageRemixStartOptions['onRejected'],
): ObjectSidebarPageRemixStartResult => {
	onRejected(reason);
	return { status: 'rejected', reason };
};

export const prepareObjectSidebarPageRemixStart = async ({
	resolveAdfDocument,
	admitLifecycle,
	conversationId,
	isCurrent,
	onRejected,
}: ObjectSidebarPageRemixStartOptions): Promise<ObjectSidebarPageRemixStartResult> => {
	let resolvedAdf: DocNode | null = null;
	try {
		resolvedAdf = await resolveAdfDocument();
	} catch {
		return rejectStart('adf-load-failed', onRejected);
	}

	if (isCurrent && !isCurrent()) {
		return rejectStart('stale-adf', onRejected);
	}

	if (!resolvedAdf) {
		return rejectStart('empty-adf', onRejected);
	}

	if (
		!isRecord(resolvedAdf) ||
		resolvedAdf.type !== 'doc' ||
		resolvedAdf.version !== 1 ||
		!Array.isArray(resolvedAdf.content) ||
		!(await isSchemaValidAdf(resolvedAdf))
	) {
		return rejectStart('malformed-adf', onRejected);
	}

	if (!resolvedAdf.content.some((node) => hasMeaningfulAdfContent(node as UnknownAdfNode))) {
		return rejectStart('empty-adf', onRejected);
	}

	try {
		if (admitLifecycle && !admitLifecycle({ conversationId })) {
			return rejectStart('lifecycle-rejected', onRejected);
		}
	} catch {
		return rejectStart('lifecycle-rejected', onRejected);
	}

	return { status: 'started', adfDocument: resolvedAdf };
};

type ObjectSidebarPageRemixNativeStartOptions = {
	startNativeRemix: () => Promise<void>;
	onNativeStartRejected: () => void;
};

export const runObjectSidebarPageRemixNativeStart = async ({
	startNativeRemix,
	onNativeStartRejected,
}: ObjectSidebarPageRemixNativeStartOptions): Promise<void> => {
	try {
		await startNativeRemix();
	} catch {
		onNativeStartRejected();
		throw new Error('Failed to start page remix from Rovo submit');
	}
};
